import { isTimeout, type RequestState, readRequestState } from "@/lib/pto/uncertain";
import { ApiError } from "@/lib/time/api-client";

/**
 * A new request sent under a browser-made id that timed out (504) may have saved. Until that is
 * settled the id stays tied to the fields it was sent with: a reopen restores them, or reports
 * the request saved; a submit with other fields gets a new id. An id is never reused with
 * different fields (the server would refuse it as changed, and keep refusing every later create).
 */

export interface CreateValues {
  typeId: string;
  startDate: string;
  endDate: string;
  hoursPerDay: number;
  description: string;
}

export interface UncertainCreate {
  id: string;
  values: CreateValues;
}

export type ReopenOutcome =
  | { kind: "fresh" }
  | { kind: "saved" }
  | { kind: "restore"; id: string; values: CreateValues };

export type SubmitOutcome =
  | { kind: "send"; id: string }
  | { kind: "sendNew" }
  | { kind: "saved"; same: boolean };

type Read = (id: string) => Promise<RequestState>;

function sameValues(a: CreateValues, b: CreateValues): boolean {
  return (
    a.typeId === b.typeId &&
    a.startDate === b.startDate &&
    a.endDate === b.endDate &&
    a.hoursPerDay === b.hoursPerDay &&
    a.description.trim() === b.description.trim()
  );
}

/** What a new-request form opens with, given the held create and its state now. */
export function reopenOutcome(
  held: UncertainCreate | null,
  state: RequestState | "unknown",
): ReopenOutcome {
  if (!held) return { kind: "fresh" };
  if (state === "unknown" || state === null) {
    return { kind: "restore", id: held.id, values: held.values };
  }
  return { kind: "saved" };
}

/** What a submit does, given the held create and (when it is this form's id) its state now. */
export function submitOutcome(
  held: UncertainCreate | null,
  formId: string,
  values: CreateValues,
  state: RequestState,
): SubmitOutcome {
  if (!held || held.id !== formId) return { kind: "send", id: formId };
  const same = sameValues(held.values, values);
  if (state !== null) return { kind: "saved", same };
  return same ? { kind: "send", id: formId } : { kind: "sendNew" };
}

// Module-level, keyed by request id, so it outlives the dialog (which unmounts when closed) and
// is written by the send itself, never by a component callback a closed dialog would skip.
const held = new Map<string, CreateValues>();

/** The most recent create that may have saved, if any. */
export function heldCreate(): UncertainCreate | null {
  let last: UncertainCreate | null = null;
  for (const [id, values] of held) last = { id, values };
  return last;
}

/** A create is about to be sent: until it answers, it may save, so its id and fields are held. */
export function noteCreateSending(id: string, values: CreateValues): void {
  held.delete(id);
  held.set(id, values);
}

/**
 * A create failed. A timeout, or no answer at all, may have saved: its id and fields stay held.
 * A definite refusal saved nothing, so the id is let go.
 */
export function noteCreateFailure(id: string, values: CreateValues, err: unknown): void {
  if (isTimeout(err) || !(err instanceof ApiError)) noteCreateSending(id, values);
  else held.delete(id);
}

/** A create succeeded (or was settled): its id is done. */
export function noteCreateSuccess(id: string): void {
  held.delete(id);
}

/** Sends a create, holding it while it is out and settling the hold from how it answers. */
export async function trackCreate<R>(
  id: string,
  values: CreateValues,
  send: () => Promise<R>,
): Promise<R> {
  noteCreateSending(id, values);
  try {
    const result = await send();
    noteCreateSuccess(id);
    return result;
  } catch (err) {
    noteCreateFailure(id, values, err);
    throw err;
  }
}

/** The dialog's open change while a create is in flight: closing waits for the answer. */
export function guardClose(
  busy: boolean,
  onOpenChange: (open: boolean) => void,
): (open: boolean) => void {
  return (open) => {
    if (!open && busy) return;
    onOpenChange(open);
  };
}

/** On opening a new-request form: settle the held create, if any. Never throws. */
export async function settleOnOpen(read: Read = readRequestState): Promise<ReopenOutcome> {
  const h = heldCreate();
  if (!h) return { kind: "fresh" };
  let state: RequestState | "unknown";
  try {
    state = await read(h.id);
  } catch {
    state = "unknown";
  }
  const out = reopenOutcome(h, state);
  if (out.kind === "saved") noteCreateSuccess(h.id);
  return out;
}

/**
 * Before sending a create: when this form's id is the held one, read it first. Throws when that
 * read fails (nothing is sent: the same id with other fields could be refused, a new id could
 * duplicate a request that landed).
 */
export async function settleOnSubmit(
  formId: string,
  values: CreateValues,
  read: Read = readRequestState,
): Promise<SubmitOutcome> {
  const v = held.get(formId);
  if (!v) return { kind: "send", id: formId };
  const h = { id: formId, values: v };
  const out = submitOutcome(h, formId, values, await read(formId));
  if (out.kind !== "send") noteCreateSuccess(formId);
  return out;
}
